import { useMemo } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowLeft } from "lucide-react";

import BaseAvatar from "@/components_v2/Avatar/BaseAvatar";
import BaseButton from "@/components_v2/Button/BaseButton";
import {
  BUTTON_SIZES,
  BUTTON_TARGETS,
  BUTTON_TYPES,
  BUTTON_VARIANTS,
} from "@/components_v2/Button/constants";
import BaseSkeleton from "@/components_v2/Skeleton/BaseSkeleton";
import BaseStatusChip from "@/components_v2/StatusChip/BaseStatusChip";
import { STATUS_CHIP_VARIANTS } from "@/components_v2/StatusChip/constants";
import BaseSkeletonGroup from "@/components_v2/Skeleton/BaseSkeletonGroup";
import { SKELETON_VARIANTS } from "@/components_v2/Skeleton/constants";
import { useGetParticipantProgressQuery } from "@/redux/service";
import { userProfilePath } from "@/domains/users/routes";
import PathTag from "@/domains/certifications/components/PathTag";
import StatusChip from "@/domains/certifications/components/StatusChip";
import { buildModuleOverviews } from "@/domains/certifications/progress";
import {
  certificationPaths,
  certificationRouteKey,
} from "@/domains/certifications/routes";
import {
  deriveParticipantStatus,
  getBanState,
  getPathCredential,
} from "@/domains/certifications/status";
import {
  formatCertificationDate,
  getInitials,
} from "@/domains/certifications/utils";
import BanBanner from "@/features/certifications/participant-detail/components/BanBanner";
import DiscussionActivity from "@/features/certifications/participant-detail/components/DiscussionActivity";
import EnrollmentActions from "@/features/certifications/participant-detail/components/EnrollmentActions";
import KnowledgeCheckList from "@/features/certifications/participant-detail/components/KnowledgeCheckList";
import WeekProgress from "@/features/certifications/participant-detail/components/WeekProgress";
import { getCheckRows } from "@/features/certifications/participant-detail/utils";
import { ParticipantDetailProps } from "@/features/certifications/participant-detail/types";
import {
  BAN_KINDS,
  ENROLLMENT_STATES,
  ISSUANCE_STATES,
  PARTICIPANT_STATUSES,
} from "@/domains/certifications/constants";

import "@/domains/certifications/components/styles.css";
import "./styles.css";

const PROGRESS_SKELETON_LINES = 6;

const ParticipantDetail = ({
  certification,
  participant,
  backTab,
}: ParticipantDetailProps) => {
  const { t } = useTranslation();
  const progress = useGetParticipantProgressQuery(participant.id);
  const credential = getPathCredential(participant);
  const ban = getBanState(participant);
  const status = deriveParticipantStatus(
    participant,
    certification.modules.length,
  );
  const issuedAt =
    credential?.state === ISSUANCE_STATES.ISSUED ? credential.issuedAt : null;

  const weeks = useMemo(
    () =>
      progress.data
        ? buildModuleOverviews(
            certification,
            progress.data,
            participant.selectedTrack,
          )
        : [],
    [certification, participant.selectedTrack, progress.data],
  );
  const checkRows = progress.data ? getCheckRows(weeks, progress.data) : [];

  let progressContent;
  if (progress.isError) {
    progressContent = (
      <section className="certification-card participant-detail-card">
        <p className="certification-muted body-s-regular" role="alert">
          {t("certification.admin.participant.progressError")}
        </p>
      </section>
    );
  } else if (!progress.data) {
    progressContent = (
      <section className="certification-card participant-detail-card">
        <BaseSkeletonGroup label={t("labels.loading")}>
          <BaseSkeleton
            variant={SKELETON_VARIANTS.TEXT}
            lines={PROGRESS_SKELETON_LINES}
          />
        </BaseSkeletonGroup>
      </section>
    );
  } else {
    progressContent = (
      <>
        <WeekProgress
          weeks={weeks}
          progress={progress.data}
          weeksCompleted={participant.weeksCompleted}
        />

        {checkRows.length > 0 && <KnowledgeCheckList rows={checkRows} />}
      </>
    );
  }

  return (
    <div className="participant-detail flex flex-col gap-500">
      <div>
        <BaseButton
          type={BUTTON_TYPES.LINK}
          variant={BUTTON_VARIANTS.LINK}
          size={BUTTON_SIZES.SMALL}
          to={`${certificationPaths.admin(certificationRouteKey(certification))}?tab=${backTab}`}
          leadingIcon={<ArrowLeft size={16} aria-hidden />}
        >
          {t(`certification.admin.participant.back.${backTab}`)}
        </BaseButton>
      </div>

      <header className="flex flex-wrap items-center gap-300">
        <BaseAvatar
          size="extra-large"
          className="participant-detail-avatar"
          label={getInitials(participant.user.fullName)}
          imageUrl={participant.user.profilePicture}
          aria-hidden
        />

        <div className="flex flex-col gap-100 flex-1">
          <h1 className="participant-detail-name header-m-medium">
            <Link
              to={userProfilePath(participant.user.id)}
              className="participant-detail-profile-link"
            >
              {participant.user.fullName}
            </Link>
          </h1>

          <Link
            to={userProfilePath(participant.user.id)}
            className="participant-detail-profile-link certification-muted body-s-regular"
          >
            {participant.user.email}
          </Link>

          <div className="flex flex-wrap items-center gap-200">
            <PathTag
              certification={certification}
              trackId={participant.selectedTrack}
            />

            <StatusChip
              status={
                status === PARTICIPANT_STATUSES.WITHDRAWN_BAN
                  ? PARTICIPANT_STATUSES.WITHDRAWN
                  : status
              }
            />

            {ban && (
              <BaseStatusChip
                label={
                  ban.kind === BAN_KINDS.TIMED
                    ? t("certification.labels.ban.until", {
                        date: formatCertificationDate(ban.endsAt),
                      })
                    : t("certification.labels.ban.permanent")
                }
                variant={STATUS_CHIP_VARIANTS.RED}
              />
            )}

            <span className="certification-muted body-xs-regular">
              {t("certification.admin.participant.enrolledOn", {
                date: formatCertificationDate(participant.enrolledAt),
              })}
            </span>

            {participant.state === ENROLLMENT_STATES.WITHDRAWN &&
              participant.withdrawnAt && (
                <span className="certification-muted body-xs-regular">
                  {t("certification.admin.participant.withdrawnOn", {
                    date: formatCertificationDate(participant.withdrawnAt),
                  })}
                </span>
              )}
          </div>
        </div>

        <EnrollmentActions
          certification={certification}
          participant={participant}
        />
      </header>

      {ban && <BanBanner ban={ban} />}

      {credential && issuedAt && (
        <section className="certification-card participant-detail-card flex flex-wrap items-center justify-between gap-300">
          <div className="flex flex-col gap-100">
            <h2 className="certification-heading body-m-medium">
              {t("certification.admin.participant.certificate")}
            </h2>

            <span className="certification-muted body-s-regular">
              {t("certification.admin.participant.issuedOn", {
                date: formatCertificationDate(issuedAt),
              })}
            </span>
          </div>

          {credential.externalUrl && (
            <BaseButton
              type={BUTTON_TYPES.LINK}
              variant={BUTTON_VARIANTS.TERTIARY}
              size={BUTTON_SIZES.SMALL}
              to={credential.externalUrl}
              target={BUTTON_TARGETS.BLANK}
            >
              {t("certification.admin.participant.viewCertificate")}
            </BaseButton>
          )}
        </section>
      )}

      {progressContent}

      <DiscussionActivity
        certification={certification}
        userId={participant.user.id}
      />
    </div>
  );
};

export default ParticipantDetail;
